import { useEffect, useState } from 'react';

import { MtxButton, MtxGrid } from '@meilleurtaux/mtp-uikit-react';
import { useLocation, useNavigate } from 'react-router-dom';

import Breadcrumb from '@/components/Breadcrumb/Breadcrumb';
import { Loader } from '@/components/Loader/loader';
import { NFD_DIACRITICS } from '@/constants/regex';
import { ROUTE } from '@/constants/route';
import { useAuth } from '@/hooks/useAuth';
import { useFaireVersementJustificatifsValidationUniversign } from '@/hooks/useFaireVersementJustificatifsValidationUniversign';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { BreadcrumbItem } from '@/services/types/Breadcrumb';
import { FaireVersementResponse } from '@/types/parcoursVersementTypes';
import { log } from '@/utils/functions/logger/logger';

import styles from './styles.module.css';

interface RouteParams {
  ':familleContrat': string;
  ':typeContrat': string;
}

export const FaireVersementValidation: React.FC = () => {
  const { user } = useAuth();
  const [showLoader, setShowLoader] = useState(false);
  const { state } = useLocation();
  const navigate = useNavigate();

  const makeDepositDocumentsValidationUniversign = useFaireVersementJustificatifsValidationUniversign();

  const rawSessionData = sessionStorage.getItem('parcoursVersementData');
  const depositData = rawSessionData ? JSON.parse(rawSessionData) : null;

  const {
    origineCapitaux: fundsOrigin = null,
    contratFamille = '',
    contratNomProduit = '',
    idSouscription = null,
    faireVersementResponse: makeDepositResponse = null,
  } = depositData ?? {};

  const { profilClient: topGaugeClient = '', profilCalcul: botGaugeCalcul = '' } = makeDepositResponse ?? {};

  const profileClassMap: Record<string, string> = {
    prudent: styles.conservative,
    equilibre: styles.balanced,
    dynamique: styles.dynamic,
    audacieux: styles.bold,
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Faire un versement', path: ROUTE.DEPOSIT },
    { label: 'Validation de l’opération', path: ROUTE.FAIREVERSEMENTVALIDATION },
  ];

  const mapRoute: RouteParams = {
    ':familleContrat': contratFamille.toLowerCase() === 'assurancevie' ? 'assurance-vie' : contratFamille.toLowerCase(),
    ':typeContrat': contratNomProduit
      .toLowerCase()
      .replaceAll(' ', '-')
      .normalize('NFD')
      .replaceAll(NFD_DIACRITICS, ''),
  };

  const canSkipJustifs = (data: FaireVersementResponse) => {
    if (data.typeIdentite === null) {
      return false;
    }

    if (
      data.typeIdentite === 'cni' &&
      (!data.validFile['justificatif-identite-1'] ||
        !data.validFile['justificatif-identite-1-recto'] ||
        !data.validFile['justificatif-identite-1-verso'])
    ) {
      return false;
    }

    if (
      data.typeIdentite === 'passeport' &&
      (!data.validFile['justificatif-identite-1'] || !data.validFile['justificatif-identite-1-recto'])
    ) {
      return false;
    }

    if (!data.validFile['justificatif-telephone'] || !data.validFile['justificatif-origine_fonds']) {
      return false;
    }

    return true;
  };

  const displayTopLabel = (profil: string) => {
    switch (profil) {
      case 'securitaire':
        return 'Sécuritaire';

      case 'prudent':
        return 'Prudent';

      case 'equilibre':
        return 'Équilibré';

      case 'dynamique':
        return 'Dynamique';

      case 'audacieux':
        return 'Audacieux';
    }
  };

  const handleSubmit = () => {
    void TRACKING_INSTANCE.triggerEvent('clic', undefined, {
      event_id: 'clic',
      event_categorie: 'clic',
      event_metier: 'espace client placement',
      event_nom: 'valider',
      event_detail_1: 'cta',
      event_detail_2: `${contratFamille} - ${contratNomProduit}`,
    });

    setShowLoader(true);

    if (canSkipJustifs(makeDepositResponse)) {
      makeDepositDocumentsValidationUniversign.mutate(idSouscription, {
        onSuccess: (universignData) => {
          setShowLoader(false);

          if (universignData.success) {
            navigate(
              ROUTE.FAIREVERSEMENTSIGNATURE.replaceAll(/:familleContrat|:typeContrat/gi, (matched) => {
                return mapRoute[matched as keyof RouteParams];
              }),
              {
                state: { data: universignData, breadcrumbItems },
              }
            );
          } else {
            navigate(
              ROUTE.FAIREVERSEMENTJUSTIFICATIFS.replaceAll(/:familleContrat|:typeContrat/gi, (matched) => {
                return mapRoute[matched as keyof RouteParams];
              }),
              {
                state: {
                  idSouscription: state.idSouscription ?? idSouscription,
                  universignErrorVersement: universignData,
                  breadcrumbItems,
                  data: makeDepositResponse,
                },
              }
            );
          }
        },
        onError: (error) => {
          log.error(`FaireVersementJustificatifsValidationUniversign mutation failed: ${error}`);

          navigate(ROUTE.ERREUR);
        },
      });
    } else {
      navigate(
        ROUTE.FAIREVERSEMENTJUSTIFICATIFS.replaceAll(/:familleContrat|:typeContrat/gi, (matched) => {
          return mapRoute[matched as keyof RouteParams];
        }),
        {
          state: {
            data: makeDepositResponse,
            fundsOrigin,
            breadcrumbItems,
            idSouscription: state.idSouscription ?? idSouscription,
          },
        }
      );
    }
  };

  useEffect(() => {
    if (!depositData) {
      navigate(ROUTE.ERREUR);
      return;
    }

    if (user?.id && user.type && user.success) {
      TRACKING_INSTANCE.setTcVars({
        page_nom: 'validation de l operation',
        page_template: 'espace client',
        user_id: user.id,
        user_client: user.type === 'Client',
        user_connexion: user.success,
        user_statut_contrat: sessionStorage.getItem('user_statut_contrat'),
        tunnel_produit: contratNomProduit,
        tunnel_metier: 'espace client placement',
        tunnel_sous_metier: contratFamille,
        tunnel_categorie_produit: 'parcours de versement',
      });
      void TRACKING_INSTANCE.triggerEvent('page', undefined, {
        event_id: 'page',
        event_categorie: 'affichage espace client',
        event_nom: 'validation de l operation',
        event_page: 'validation de l operation',
      });
    }
  }, [user?.id, user?.type, user?.success]);

  return (
    <MtxGrid className={styles.container}>
      {showLoader && <Loader />}

      <div className={styles.header}>
        <Breadcrumb items={breadcrumbItems} />

        <h1>Validation de l&apos;opération</h1>
      </div>

      <div className={styles.body}>
        <div className={styles.gaugeAndText}>
          <MtxGrid row>
            <MtxGrid className={styles.gauge} col={12} colXs={12} colSm={12} colMd={12} colLg={12} colXl={12}>
              <div className={`${styles.itemRange} ${styles.itemInput} ${styles.itemRangeSrri}`}>
                <div
                  className={`${styles.contentRangeText} ${styles.contentRangeTextTop} ${profileClassMap[topGaugeClient] ?? ''}`}
                >
                  <span className={styles.rangeText}>
                    Votre profil de risque : <span data-range="rangesrri">{displayTopLabel(topGaugeClient)}</span>
                  </span>

                  <div
                    className={
                      topGaugeClient === 'securitaire'
                        ? ''
                        : `${styles.arrowProfil} ${profileClassMap[topGaugeClient] ?? ''}`
                    }
                  ></div>
                </div>
              </div>

              <div className={styles.contentRange}>
                <div className={styles.inputRange}></div>

                <div className={styles.listingRangeStep}>
                  <span className={`${styles.rangeStep} ${styles.rangeStep1}`}></span>
                  <span className={`${styles.rangeStep} ${styles.rangeStep2}`}></span>
                  <span className={`${styles.rangeStep} ${styles.rangeStep3}`}></span>
                  <span className={`${styles.rangeStep} ${styles.rangeStep4}`}></span>
                </div>
              </div>

              <div className={`${styles.itemRange} ${styles.itemInput} ${styles.itemRangeSrriAllocation}`}>
                <div className={`${styles.contentRangeText} ${profileClassMap[botGaugeCalcul] ?? ''}`}>
                  <div className={`${styles.arrowProfil} ${profileClassMap[botGaugeCalcul] ?? ''}`}></div>
                  <span className={styles.rangeText}>
                    <span className={styles.textAllocation}>Votre nouvelle répartition</span>
                  </span>
                </div>
              </div>
            </MtxGrid>
          </MtxGrid>

          <div className={styles.gaugeText}>
            <span>Votre profil de risque préconisé est : {displayTopLabel(topGaugeClient)}</span>
            <p>
              En validant cette opération, vous acceptez que le niveau de risque de votre nouvelle allocation soit
              supérieur au profil choisi à la souscription.
            </p>
          </div>
        </div>

        <MtxGrid row>
          <MtxGrid col={4} colXs={12} colSm={6} colMd={6} colLg={4} colXl={4}>
            <MtxButton onClick={() => handleSubmit()} variant="primary" size="large" disabled={false} fullWidth={true}>
              <div>Valider</div>
            </MtxButton>
          </MtxGrid>
        </MtxGrid>
      </div>
    </MtxGrid>
  );
};
